import React from "react";

import {
  Card,
  CardHeader,
  CardContent,
  makeStyles,
  CardActions,
  Button,
  Checkbox,
  Typography,
  List,
  ListItem,
} from "@material-ui/core";

import { Link } from "react-router-dom";

import { WebTracker } from "../../types";
import {
  FollowUpTasksProgress,
  WebTrackerFollowUpTask,
  hasNoFollowUpTasks,
  hasIncompleteTask,
  displayName,
  getFollowUpTasks,
} from "./FollowUpTasksTypes";
import { capitalizeFirstLetter } from "../../../util";

import useWTMURLs from "../../useWTMURLs";

const useStyles = makeStyles((theme) => ({
  cardHeader: {
    backgroundColor: theme.palette.grey[50],
    borderBottomColor: theme.palette.grey[200],
    borderBottomStyle: "solid",
    borderBottomWidth: 1,
    padding: theme.spacing(2),
  },
  cardContent: {
    padding: theme.spacing(2, 2),
  },
  cardActions: {
    justifyContent: "flex-end",
    display: "flex",
    padding: theme.spacing(2, 2),
  },
  listItems: {
    padding: 0,
  },
  list: {
    padding: 0,
  },
  checkbox: {
    padding: theme.spacing(0.7),
  },
}));

export type FollowUpTasksAsListItemsProps = {
  tasksProgress: FollowUpTasksProgress;
};

export function FollowUpTasksAsListItems({
  tasksProgress,
}: FollowUpTasksAsListItemsProps) {
  const classes = useStyles();
  const tasks = Array.from(tasksProgress.keys());

  return (
    <List className={classes.list}>
      {tasks.map((task: WebTrackerFollowUpTask, index: number) => {
        const completed = tasksProgress.get(task) as boolean;

        return (
          <ListItem
            key={`follow-up-task-${index}`}
            className={classes.listItems}
            style={{ textDecoration: completed ? "line-through" : "undefined" }}
          >
            <Checkbox
              checked={completed}
              disabled
              size="small"
              className={classes.checkbox}
            />
            <Typography variant="body1">
              {capitalizeFirstLetter(displayName(task))}
            </Typography>
          </ListItem>
        );
      })}
    </List>
  );
}

export type FollowUpTaskActionsProps = {
  tasksProgress: FollowUpTasksProgress;
  openFollowUpTaskDetailsModal: () => void;
  setFollowUpTaskForModal: React.Dispatch<
    React.SetStateAction<WebTrackerFollowUpTask | null>
  >;
  onClickBAAUpload: () => void;
};

function FollowUpTaskActions({
  tasksProgress,
  openFollowUpTaskDetailsModal,
  setFollowUpTaskForModal,
  onClickBAAUpload,
}: FollowUpTaskActionsProps) {
  const classes = useStyles();
  const wtmURLs = useWTMURLs();

  const shouldShowSetupDestinationBtn = hasIncompleteTask(
    tasksProgress,
    "set_up_destination"
  );
  const shouldShowSetupVideoProxyBtn = hasIncompleteTask(
    tasksProgress,
    "set_up_video_proxy"
  );
  const shouldShowSetupFPMapsBtn = hasIncompleteTask(
    tasksProgress,
    "set_up_fp_maps"
  );
  const shouldShowPublishTrackerBtn = hasIncompleteTask(
    tasksProgress,
    "publish_changes"
  );
  const shouldShowUploadBAABtn = hasIncompleteTask(
    tasksProgress,
    "upload_document"
  );
  const shouldShowUninstallTrackerBtn =
    hasIncompleteTask(tasksProgress, "uninstall_tracker") ||
    hasIncompleteTask(tasksProgress, "uninstall_native_tracker");

  const shouldShowCardActions =
    shouldShowSetupDestinationBtn ||
    shouldShowSetupVideoProxyBtn ||
    shouldShowUninstallTrackerBtn ||
    shouldShowPublishTrackerBtn ||
    shouldShowUploadBAABtn;

  const onClickHandler = function (followUpTask: WebTrackerFollowUpTask) {
    setFollowUpTaskForModal(followUpTask);
    openFollowUpTaskDetailsModal();
  };

  return (
    <>
      {shouldShowCardActions && (
        <CardActions className={classes.cardActions}>
          {shouldShowSetupDestinationBtn && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => onClickHandler("set_up_destination")}
              size="small"
              title="Setup Destination"
            >
              Setup Destination
            </Button>
          )}
          {shouldShowSetupVideoProxyBtn && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => onClickHandler("set_up_video_proxy")}
              size="small"
              title="Setup Video Proxy"
            >
              Setup Video Proxy
            </Button>
          )}
          {shouldShowSetupFPMapsBtn && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => onClickHandler("set_up_fp_maps")}
              size="small"
              title="Setup Freshpaint Maps"
            >
              Setup Freshpaint Maps
            </Button>
          )}
          {shouldShowPublishTrackerBtn && (
            <Link to={wtmURLs.trackerBlocking}>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                title="Publish Changes"
              >
                Publish Changes
              </Button>
            </Link>
          )}
          {shouldShowUninstallTrackerBtn && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => onClickHandler("uninstall_tracker")}
              size="small"
              title="How To Uninstall"
            >
              How To Uninstall
            </Button>
          )}
          {shouldShowUploadBAABtn && (
            <Button
              variant="contained"
              color="primary"
              onClick={onClickBAAUpload}
              size="small"
              title="UploadBAA"
            >
              Upload BAA
            </Button>
          )}
        </CardActions>
      )}
    </>
  );
}

export type FollowUpTasksProps = {
  webTracker: WebTracker;
  openFollowUpTaskDetailsModal: () => void;
  setFollowUpTaskForModal: React.Dispatch<
    React.SetStateAction<WebTrackerFollowUpTask | null>
  >;
  onClickBAAUpload: () => void;
};

function FollowUpTasks({
  webTracker,
  openFollowUpTaskDetailsModal,
  setFollowUpTaskForModal,
  onClickBAAUpload,
}: FollowUpTasksProps) {
  const classes = useStyles();

  const tasksProgress = getFollowUpTasks(
    webTracker.resolution?.resolutionType ?? "unresolved",
    webTracker
  );

  if (hasNoFollowUpTasks(tasksProgress)) {
    return <></>;
  }

  return (
    <Card>
      <CardHeader className={classes.cardHeader} title="Action Items" />
      <CardContent>
        <FollowUpTasksAsListItems tasksProgress={tasksProgress} />
      </CardContent>
      <FollowUpTaskActions
        tasksProgress={tasksProgress}
        openFollowUpTaskDetailsModal={openFollowUpTaskDetailsModal}
        setFollowUpTaskForModal={setFollowUpTaskForModal}
        onClickBAAUpload={onClickBAAUpload}
      />
    </Card>
  );
}

export default FollowUpTasks;
